import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const slides = [
  {
    image: '/hero-slides/slide1.webp',
    tag: 'Zihinsel Mimari',
    title: 'Zihnin...',
    highlight: 'senin en güçlü silahın.',
    sub: 'Her düşünce bir karar. Her karar bir kader.',
    mobileBg: '35% 20%',
  },
  {
    image: '/hero-slides/slide2.webp',
    tag: 'Bilinçaltı Programlama',
    title: 'Sınırların sadece',
    highlight: 'zihninde var.',
    sub: 'Bilinçaltın sana itaat ediyor. Doğru komutu ver.',
    mobileBg: '20% 15%',
  },
  {
    image: '/hero-slides/slide3.webp',
    tag: 'Limitless',
    title: 'Zincirler kırılmadan...',
    highlight: 'potansiyel açığa çıkmaz.',
    sub: 'Zihnini yeniden programla.',
    mobileBg: '20% 15%',
  },
  {
    image: '/hero-slides/slide4.webp',
    tag: 'Dönüşüm',
    title: 'Her an bir fırsat.',
    highlight: 'Her fırsat bir dönüşüm.',
    sub: 'Fark et. Değiş. Dönüş.',
    mobileBg: '65% 20%',
  },
  {
    image: '/hero-slides/slide5.webp',
    tag: 'Hipnoz & Farkındalık',
    title: 'Bilinçaltı konuşurken...',
    highlight: 'her şey değişir.',
    sub: 'Gerçek güç, içinde saklı.',
    mobileBg: '35% 20%',
  },
  {
    image: '/hero-slides/slide6.webp',
    tag: 'Nöropazarlama',
    title: 'Kalabalıklar seni izliyor.',
    highlight: 'Ama sen nereye bakıyorsun?',
    sub: 'Odak = Güç.',
    mobileBg: '40% 20%',
  },
  {
    image: '/hero-slides/slide7.webp',
    tag: 'İkna Stratejileri',
    title: 'Sen fark ettiğinde...',
    highlight: 'her şey başlar.',
    sub: 'Değişim sessizce gelir.',
    mobileBg: '55% 20%',
  },
  {
    image: '/hero-slides/slide8.webp',
    tag: 'Zihinsel Dönüşüm',
    title: 'Bir eşik var...',
    highlight: 've sen tam orada duruyorsun.',
    sub: 'Adım at.',
    mobileBg: '65% 20%',
  },
  {
    image: '/hero-slides/slide9.webp',
    tag: 'Taner Torun',
    title: 'Limitless. Sınırsız.',
    highlight: 'Sen.',
    sub: 'Zihnin hazır mı?',
    mobileBg: '40% 20%',
  },
];

const SLIDE_DURATION = 6000;

const HeroSlider = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const goTo = useCallback((index) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  }, [current]);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % slides.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    intervalRef.current = setInterval(next, SLIDE_DURATION);
    return () => clearInterval(intervalRef.current);
  }, [next, isPaused]);

  const slide = slides[current];

  const imageVariants = {
    enter: (dir) => ({ opacity: 0, scale: 1.1, x: dir > 0 ? 60 : -60 }),
    center: { opacity: 1, scale: 1.05, x: 0, transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] } },
    exit: (dir) => ({ opacity: 0, scale: 1, x: dir > 0 ? -60 : 60, transition: { duration: 0.8, ease: 'easeIn' } }),
  };

  const textVariants = {
    enter: { opacity: 0, y: 40, filter: 'blur(8px)' },
    center: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, y: -20, filter: 'blur(4px)', transition: { duration: 0.4 } },
  };

  return (
    <>
      <div
        className="relative min-h-[85vh] md:min-h-screen flex items-end overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        data-testid="hero-slider"
      >
        {/* Background Images */}
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={current}
            custom={direction}
            variants={imageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="absolute inset-0"
          >
            <div
              className="absolute inset-0 bg-cover will-change-transform"
              style={{ 
                backgroundImage: `url(${slide.image})`,
                backgroundPosition: isMobile ? slide.mobileBg : 'center',
              }}
            />
            {/* Dark mood overlay - balanced */}
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        {/* Content - Left aligned, bottom positioned */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pb-24 md:pb-32">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              variants={textVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="max-w-2xl"
            >
              {/* Tag */}
              <div className="inline-flex items-center gap-2 bg-white/[0.06] backdrop-blur-md border border-white/10 px-3 py-1.5 rounded-full mb-5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-purple-500" />
                </span>
                <span className="text-purple-300/80 text-[11px] font-light tracking-[0.2em] uppercase">{slide.tag}</span>
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] drop-shadow-[0_2px_20px_rgba(0,0,0,0.8)]">
                {slide.title}
                <br />
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent">
                  {slide.highlight}
                </span>
              </h1>

              {/* Subtitle - Hypnotic */}
              <p className="text-sm sm:text-base md:text-lg text-white/50 font-light mt-4 italic tracking-wide drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)]">
                {slide.sub}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* CTA Row - Hidden on mobile (sticky CTA handles it) */}
          <div className="mt-8 hidden md:block">
            <button
              onClick={() => navigate('/masterclass')}
              className="group relative"
              data-testid="hero-slider-cta"
            >
              {/* Outer glow - breathing purple */}
              <div className="absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-purple-500/40 via-fuchsia-500/30 to-purple-600/40 animate-ctaPulse blur-md" />
              
              {/* Gradient border container */}
              <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-purple-500/50 via-fuchsia-400/40 to-purple-600/50" />
              
              {/* Main button body */}
              <div className="relative bg-[#0d0518]/90 backdrop-blur-2xl px-10 md:px-14 py-5 rounded-2xl overflow-hidden transition-all duration-500 group-hover:bg-[#12061f]/90">
                
                {/* Top inner highlight */}
                <div className="absolute top-0 left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-purple-300/30 to-transparent" />
                
                {/* Shimmer sweep */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.04] to-transparent animate-ctaSweep" />
                
                {/* Content */}
                <div className="relative flex items-center justify-between gap-6">
                  <span className="text-white text-base md:text-lg font-semibold tracking-wide">
                    MASTERCLASS EĞİTİMİNE KATILIN
                  </span>
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-md group-hover:bg-purple-500/30 transition-all duration-500" />
                    <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/30 to-fuchsia-500/20 border border-purple-400/25 flex items-center justify-center group-hover:from-purple-500/40 group-hover:to-fuchsia-500/30 group-hover:border-purple-400/40 transition-all duration-500">
                      <ArrowRight className="w-4 h-4 text-purple-200 group-hover:translate-x-0.5 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
                
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-[5%] right-[5%] h-[1px] bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
              </div>
            </button>
          </div>

          {/* Micro text - Hidden on mobile */}
          <p className="hidden md:block text-white/15 text-[10px] font-light mt-6 max-w-md" data-testid="hero-premium-disclaimer">
            Bu içerikler kişisel gelişim ve farkındalık odaklıdır. Tıbbi teşhis ve tedavi kapsamında değildir.
          </p>
        </div>

        {/* Slide Navigation - Arrows */}
        <button
          onClick={prev}
          className="absolute left-3 sm:left-6 top-[30%] md:top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/[0.04] backdrop-blur-md border border-white/10 flex items-center justify-center text-white/30 hover:text-white hover:bg-white/10 transition-all duration-300"
          data-testid="hero-slider-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          onClick={next}
          className="absolute right-3 sm:right-6 top-[30%] md:top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/[0.04] backdrop-blur-md border border-white/10 flex items-center justify-center text-white/30 hover:text-white hover:bg-white/10 transition-all duration-300"
          data-testid="hero-slider-next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-6 sm:left-10 lg:left-16 z-20 flex items-center gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="group relative"
              data-testid={`hero-slide-dot-${i}`}
            >
              <div className={`h-[3px] rounded-full transition-all duration-500 ${
                i === current ? 'w-8 bg-purple-400' : 'w-2 bg-white/20 group-hover:bg-white/40'
              }`} />
            </button>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 z-20 h-[2px] bg-white/5">
          <motion.div
            key={current}
            className="h-full bg-gradient-to-r from-purple-500/60 to-pink-500/60"
            initial={{ width: '0%' }}
            animate={{ width: isPaused ? undefined : '100%' }}
            transition={{ duration: SLIDE_DURATION / 1000, ease: 'linear' }}
          />
        </div>
      </div>
    </>
  );
};

export default HeroSlider;
